import confirmLogoAvatar from 'assets/img/brand/logo_icon_avatar_2026.svg';
import { resolveChatLinkAction } from 'components/chatMessageLink';
import { whenIntercomReady } from 'components/intercom/ready';
import * as consts from 'consts/consts';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, UncontrolledTooltip } from 'reactstrap';
import { useProxyParameters } from 'utils/api/ApiHooks';
import { useLocalStorageState } from 'utils/util/hooks';
import { relativeDateTime } from 'utils/util/time';
import { downloadLink, stripHtml } from 'utils/util/util';
import { useAiBackgroundTask } from 'views/AI/common';
import AILoader from 'views/AI/widgets/AILoader';
import MarkdownViewer from 'views/Widgets/Inputs/MarkdownViewer';
import RichTextEditor from 'views/Widgets/Inputs/RichTextEditor';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import Avatar from 'views/Widgets/People/Avatar';
import ConfirmAPI from '../utils/api/ConfirmAPI';

interface DisplayMessage {
  key: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

type ChatScreen = 'chat' | 'sessions';

const Chat: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<DisplayMessage[]>([
    {
      key: 'initial',
      text: formatMessage({
        id: 'app.components.chat.initial_greeting',
        defaultMessage: 'Hi! How can I help you today?',
      }),
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const messagesEndRef = useRef(null);
  const chatInputNodeRef = useRef<HTMLDivElement | null>(null);
  const chatInputKeydownRef = useRef<((e: KeyboardEvent) => void) | null>(null);
  const chatInputRef = useCallback((node: HTMLDivElement | null) => {
    // Detach from previous node
    if (chatInputNodeRef.current && chatInputKeydownRef.current) {
      chatInputNodeRef.current.removeEventListener(
        'keydown',
        chatInputKeydownRef.current,
        true
      );
    }
    chatInputNodeRef.current = node;
    if (node) {
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          const mentionMenuOpen = Array.from(
            document.querySelectorAll<HTMLElement>('.tribute-container')
          ).some((menu) => menu.style.display !== 'none');
          if (mentionMenuOpen) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          // Read current content directly from Froala DOM to avoid
          // stale React state (onModelChange is async/debounced).
          const editorEl = node.querySelector('.fr-element');
          const content = editorEl?.innerHTML || '';
          // Clear Froala DOM immediately so its async onModelChange
          // doesn't re-populate inputValue after we clear it.
          if (editorEl) editorEl.innerHTML = '';
          handleSendRef.current(content);
        }
      };
      chatInputKeydownRef.current = handler;
      node.addEventListener('keydown', handler, true);
    }
  }, []);
  const [currentScreen, setCurrentScreen] = useState<ChatScreen>('sessions');
  const [sessions, setSessions] = useState<any[]>([]); // Replace 'any' with appropriate session type
  const [progressContent, setProgressContent] = useState<string | undefined>(
    undefined
  );
  const [deletingSessionKey, setDeletingSessionKey] = useState<string | null>(
    null
  );
  const [messageFeedback, setMessageFeedback] = useState<
    Record<string, 'LIKE' | 'DISLIKE'>
  >({});
  const [isDocked, setIsDocked] = useLocalStorageState<boolean>(
    'confirm-chat-docked',
    false
  );
  const [dockedWidth, setDockedWidth] = useLocalStorageState<number>(
    'confirm-chat-docked-width',
    400
  );
  const isResizing = useRef(false);
  const handleSendRef = useRef<(contentOverride?: string) => void>(() => {});

  const MIN_DOCKED_WIDTH = 300;
  const MAX_DOCKED_WIDTH = 800;

  const [chatBubbleVisible, setChatBubbleVisible] = useState(false);

  // Detect Intercom chat visibility via its show/hide callbacks. The unified
  // messenger mounts after its JWT fetch, so subscribe once it is there.
  useEffect(
    () =>
      whenIntercomReady(() => {
        // @ts-expect-error
        window.Intercom('onShow', () => setChatBubbleVisible(true));
        // @ts-expect-error
        window.Intercom('onHide', () => setChatBubbleVisible(false));
      }),
    []
  );

  const history = useHistory();

  // Track which session owns the in-flight background task
  const taskSessionRef = useRef<string | null>(null);

  const isNewSession =
    !sessionKey && !messages.some((m) => m.sender === 'user');

  const handleBrowsePrompts = useCallback(() => {
    history.push(consts.TEAM_AI_PROMPTS(formatMessage).path);
  }, [history, formatMessage]);

  const focusChatInput = useCallback(() => {
    setTimeout(() => {
      const el = chatInputNodeRef.current?.querySelector(
        '.fr-element[contenteditable]'
      ) as HTMLElement | null;
      el?.focus();
    }, 100);
  }, []);

  const scrollToBottom = useCallback(() => {
    // @ts-expect-error
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  const proxyParams = useProxyParameters();

  const sendFeedback = useCallback(
    (messageKey: string, feedback: 'LIKE' | 'DISLIKE' | null) => {
      ConfirmAPI.sendRequestToConfirm(
        'PATCH',
        `chat/messages/${messageKey}/feedback`,
        { ...proxyParams, feedback },
        (_response, error) => {
          if (error) {
            console.error('Error sending feedback:', error);
          }
        }
      );
    },
    [proxyParams]
  );

  const handleFeedback = useCallback(
    (messageKey: string, type: 'LIKE' | 'DISLIKE') => {
      setMessageFeedback((prev) => {
        const newValue = prev[messageKey] === type ? undefined! : type;
        sendFeedback(messageKey, newValue ?? null);
        return { ...prev, [messageKey]: newValue };
      });
    },
    [sendFeedback]
  );

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus the input when the chat screen is shown or chat is opened
  useEffect(() => {
    if (isOpen && currentScreen === 'chat') {
      focusChatInput();
    }
  }, [isOpen, currentScreen, focusChatInput]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle CSS class on .app container and set panel width custom property
  useEffect(() => {
    const appEl = document.querySelector('.app') as HTMLElement | null;
    if (!appEl) return;
    if (isDocked && isOpen) {
      appEl.classList.add('chat-panel-docked');
      appEl.style.setProperty('--chat-panel-width', `${dockedWidth ?? 400}px`);
    } else {
      appEl.classList.remove('chat-panel-docked');
      appEl.style.removeProperty('--chat-panel-width');
    }
    return () => {
      appEl.classList.remove('chat-panel-docked');
      appEl.style.removeProperty('--chat-panel-width');
    };
  }, [isDocked, isOpen, dockedWidth]);

  // Handle resize dragging
  useEffect(() => {
    if (!isDocked || !isOpen) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      e.preventDefault();
      const newWidth = Math.min(
        MAX_DOCKED_WIDTH,
        Math.max(MIN_DOCKED_WIDTH, window.innerWidth - e.clientX)
      );
      setDockedWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDocked, isOpen, setDockedWidth]);

  // Auto-undock on small screens
  useEffect(() => {
    if (isDocked && windowSize.width < 992) {
      setIsDocked(false);
    }
  }, [isDocked, setIsDocked, windowSize.width]);

  // Listen for external requests to open the chat with a pre-filled prompt
  useEffect(() => {
    const handleOpenChat = (e: Event) => {
      const detail = (e as CustomEvent<{ text: string }>).detail;
      setSessionKey(null);
      setMessages([
        {
          key: 'initial',
          text: formatMessage({
            id: 'app.components.chat.initial_greeting',
            defaultMessage: 'Hi! How can I help you today?',
          }),
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
      setCurrentScreen('chat');
      setIsOpen(true);
      setInputValue(detail.text);
    };

    window.addEventListener('open-ai-chat', handleOpenChat);
    return () => window.removeEventListener('open-ai-chat', handleOpenChat);
  }, [formatMessage]);

  const getSessions = useCallback(() => {
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      'chat/sessions',
      { ...proxyParams },
      (response, error) => {
        if (error) {
          // Handle error appropriately
          console.error('Error fetching chat sessions:', error);
        } else if (response) {
          setSessions(response);
        }
      }
    );
  }, [proxyParams]);

  const handleDeleteSession = useCallback(
    (sessionKeyToDelete: string) => {
      ConfirmAPI.sendRequestToConfirm(
        'DELETE',
        `chat/sessions/${sessionKeyToDelete}`,
        { ...proxyParams },
        (_response, error) => {
          if (error) {
            console.error('Error deleting chat session:', error);
            toast.error(
              formatMessage({
                id: 'app.components.chat.delete.error',
                defaultMessage: 'Failed to delete chat',
              })
            );
          } else {
            setSessions((prev) =>
              prev.filter((s) => s.key !== sessionKeyToDelete)
            );
            toast.success(
              formatMessage({
                id: 'app.components.chat.delete.success',
                defaultMessage: 'Chat deleted',
              })
            );
          }
          setDeletingSessionKey(null);
        }
      );
    },
    [proxyParams, formatMessage]
  );

  const handleOnProgressContentUpdate = useCallback((content: string) => {
    setProgressContent(content);
  }, []);

  const { requestSuggestion, resumeTask, cancelTask, backgroundTaskKey } =
    useAiBackgroundTask({
      suggestionType: 'AGENT_ASSISTANT',
      requestParameters: {},
      onProgressContentUpdate: handleOnProgressContentUpdate,
    });

  const handleTaskCompleted = useCallback(
    (response) => {
      const botMessage: DisplayMessage = {
        key: response.AGENT_ASSISTANT.message.key,
        text: response.AGENT_ASSISTANT.message.content,
        sender: 'bot',
        timestamp: new Date(response.AGENT_ASSISTANT.message.created_at),
      };
      setMessages((prev) => [...prev, botMessage]);
      setProgressContent(undefined);
      setIsLoading(false);
      taskSessionRef.current = null;
      focusChatInput();
    },
    [focusChatInput]
  );

  const handleTaskError = useCallback(
    (error) => {
      console.error('AI background task error:', error);
      const errorBotMessage: DisplayMessage = {
        key: `error-${Date.now()}`,
        text: `Error: ${error}`,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorBotMessage]);
      setIsLoading(false);
      setProgressContent(undefined);
      taskSessionRef.current = null;
      focusChatInput();
    },
    [focusChatInput]
  );

  const getSession = useCallback(
    (sessionKey: string) => {
      ConfirmAPI.sendRequestToConfirm(
        'GET',
        `chat/sessions/${sessionKey}`,
        { ...proxyParams },
        (response, error) => {
          if (error) {
            console.error('Error fetching chat sessions:', error);
          } else if (response) {
            setSessionKey(response.key);
            setMessages(
              response.messages.map((msg: any) => ({
                key: msg.key,
                text: msg.content,
                sender: msg.role === 'USER' ? 'user' : 'bot',
                timestamp: new Date(msg.created_at),
              }))
            );

            // Restore persisted feedback state
            const feedbackMap: Record<string, 'LIKE' | 'DISLIKE'> = {};
            response.messages.forEach((msg: any) => {
              if (msg.feedback) {
                feedbackMap[msg.key] = msg.feedback;
              }
            });
            setMessageFeedback(feedbackMap);

            // Resume polling if this session has an active background task
            if (
              response.background_task_key &&
              response.background_task_status &&
              ['S', 'P'].includes(response.background_task_status)
            ) {
              taskSessionRef.current = response.key;
              setIsLoading(true);
              resumeTask(
                response.background_task_key,
                handleTaskCompleted,
                () => {},
                handleTaskError
              );
            }
          }
        }
      );
    },
    [proxyParams, resumeTask, handleTaskCompleted, handleTaskError]
  );

  useEffect(() => {
    if (currentScreen === 'sessions') {
      getSessions();
    } else if (currentScreen === 'chat' && sessionKey) {
      getSession(sessionKey);
    }
  }, [currentScreen, getSessions, getSession, sessionKey]);

  const handleSendCompleted = useCallback(
    (response) => {
      if (!taskSessionRef.current) {
        // New session — set the session key returned by the backend
        setSessionKey(response.AGENT_ASSISTANT.session_key);
      }
      handleTaskCompleted(response);
    },
    [handleTaskCompleted]
  );

  const handleSend = useCallback(
    (contentOverride?: string) => {
      const content = contentOverride ?? inputValue;
      if (content.trim() === '' || isLoading) return;

      const userMessage: DisplayMessage = {
        key: `user-${Date.now()}`,
        text: content,
        sender: 'user',
        timestamp: new Date(),
      };

      taskSessionRef.current = sessionKey;
      setMessages((prev) => [...prev, userMessage]);
      setInputValue('');
      setIsLoading(true);

      requestSuggestion(
        { session_key: sessionKey, user_prompt: content },
        handleSendCompleted,
        () => {},
        handleTaskError
      );
    },
    [
      inputValue,
      isLoading,
      requestSuggestion,
      sessionKey,
      handleSendCompleted,
      handleTaskError,
    ]
  );

  handleSendRef.current = handleSend;

  const handleSendClick = useCallback(() => {
    handleSend();
  }, [handleSend]);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value || '');
  }, []);

  const handleCancelDelete = useCallback(() => setDeletingSessionKey(null), []);

  const handleConfirmDelete = useCallback(() => {
    if (deletingSessionKey) {
      handleDeleteSession(deletingSessionKey);
    }
  }, [deletingSessionKey, handleDeleteSession]);

  const handleNewChat = useCallback(() => {
    setSessionKey(null);
    setMessages([
      {
        key: 'initial',
        text: formatMessage({
          id: 'app.components.chat.initial_greeting',
          defaultMessage: 'Hi! How can I help you today?',
        }),
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
    setCurrentScreen('chat');
  }, [formatMessage]);

  const handleNavigateToSessions = useCallback(() => {
    // Stop polling if there's an in-flight task so the loader
    // doesn't bleed into the sessions list or other conversations.
    if (backgroundTaskKey) {
      cancelTask();
      setIsLoading(false);
      setProgressContent(undefined);
      taskSessionRef.current = null;
    }
    setCurrentScreen('sessions');
  }, [backgroundTaskKey, cancelTask]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleToggleDock = useCallback(() => {
    setIsDocked((prev) => {
      if (!prev) {
        setIsOpen(true);
        setIsExpanded(false);
      }
      return !prev;
    });
  }, [setIsDocked]);

  const handleMessageLinkClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const action = resolveChatLinkAction(href, window.location.origin);
      e.preventDefault();
      switch (action.kind) {
        case 'download':
          downloadLink({ filename: action.filename, url: action.url });
          return;
        case 'external':
          window.open(action.url, '_blank', 'noopener,noreferrer');
          return;
        case 'internal':
          history.push(action.path);
          return;
      }
    },
    [history]
  );

  // Calculate dimensions based on expansion state
  const chatWidth = useMemo(
    () => (isExpanded ? Math.min(450 * 3, windowSize.width - 100) : 450),
    [isExpanded, windowSize.width]
  );
  const chatHeight = useMemo(
    () => (isExpanded ? Math.min(600 * 3, windowSize.height - 100) : 600),
    [isExpanded, windowSize.height]
  );

  return (
    <div
      className={isDocked && isOpen ? 'chat-docked-panel' : undefined}
      style={
        isDocked && isOpen
          ? { width: `${dockedWidth ?? 400}px` }
          : {
              position: 'fixed',
              bottom: '20px',
              right: chatBubbleVisible ? '90px' : '20px',
              zIndex: 1040,
            }
      }
    >
      {isDocked && isOpen && (
        <div className="chat-resize-handle" onMouseDown={handleResizeStart} />
      )}
      {isOpen && (
        <div
          className={`card d-flex flex-column${
            isDocked && isOpen ? '' : ' shadow-lg'
          }`}
          style={
            isDocked && isOpen
              ? {
                  width: '100%',
                  height: '100%',
                  margin: 0,
                  borderRadius: 0,
                }
              : {
                  width: `${chatWidth}px`,
                  height: `${chatHeight}px`,
                  marginBottom: '10px',
                }
          }
        >
          <div className="card-header navbar-dark text-white d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {currentScreen === 'chat' && (
                <button
                  className="btn btn-sm btn-link text-white p-0 navbar-dark me-2"
                  onClick={handleNavigateToSessions}
                  style={{ textDecoration: 'none', fontSize: '1.2rem' }}
                  title={formatMessage({
                    id: 'app.components.chat.title.back_to_chat_list',
                    defaultMessage: 'Back to Chat list',
                  })}
                >
                  <i className="fe fe-arrow-left" />
                </button>
              )}

              <Avatar
                size="xs"
                person={{
                  full_name: 'AI Agent',
                  given_name: 'AI Agent',
                  family_name: '',
                  title: '',
                  avatar: confirmLogoAvatar,
                }}
              />
              <h5 className="ms-3 mb-0 pt-1">
                <FormattedMessage
                  id="app.components.chat.ai_agent"
                  defaultMessage="AI Agent"
                />
              </h5>
            </div>
            <div className="d-flex align-items-center">
              {windowSize.width >= 992 && (
                <button
                  className="btn btn-sm btn-link text-white p-0 navbar-dark me-2"
                  onClick={handleToggleDock}
                  style={{ textDecoration: 'none', fontSize: '1.2rem' }}
                  title={
                    isDocked
                      ? formatMessage({
                          id: 'app.components.chat.title.undock_panel',
                          defaultMessage: 'Undock to floating',
                        })
                      : formatMessage({
                          id: 'app.components.chat.title.dock_panel',
                          defaultMessage: 'Dock to side',
                        })
                  }
                >
                  <i
                    className={isDocked ? 'fe fe-minimize-2' : 'fe fe-sidebar'}
                  />
                </button>
              )}
              {!isDocked && (
                <button
                  className="btn btn-sm btn-link text-white p-0 navbar-dark me-2"
                  onClick={handleToggleExpand}
                  style={{ textDecoration: 'none', fontSize: '1.2rem' }}
                  title={
                    isExpanded
                      ? formatMessage({
                          id: 'app.components.chat.title.collapse',
                          defaultMessage: 'Collapse',
                        })
                      : formatMessage({
                          id: 'app.components.chat.title.expand',
                          defaultMessage: 'Expand',
                        })
                  }
                >
                  {isExpanded ? (
                    <i className="fe fe-minimize-2" />
                  ) : (
                    <i className="fe fe-maximize-2" />
                  )}
                </button>
              )}
              <button
                className="btn btn-sm btn-link text-white p-0 navbar-dark ms-3"
                onClick={handleClose}
                style={{ textDecoration: 'none', fontSize: '1.2rem' }}
                title={formatMessage({
                  id: 'app.components.chat.title.close_popover',
                  defaultMessage: 'Close popover',
                })}
              >
                <i className="fe fe-x" />
              </button>
            </div>
          </div>
          {currentScreen === 'sessions' && (
            <div
              className="card-body overflow-auto bg-white"
              style={
                isDocked && isOpen
                  ? { flex: 1 }
                  : { height: `${chatHeight - 100}px` }
              }
            >
              <style>{`
                .chat-session-new-btn {
                  cursor: pointer;
                  border-style: dashed !important;
                  transition: background-color 0.15s, transform 0.15s;
                }
                .chat-session-new-btn:hover {
                  transform: translateY(-1px);
                }
                .chat-session-item {
                  cursor: pointer;
                  transition: background-color 0.15s, box-shadow 0.15s;
                }
                .chat-session-item:hover {
                  background-color: #f0f4ff;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                }
                .chat-session-title {
                  font-weight: 500;
                }
                .chat-session-chevron {
                  opacity: 0.5;
                }
                .chat-session-empty-icon {
                  font-size: 2rem;
                  opacity: 0.4;
                }
                .chat-session-label {
                  font-size: 0.75rem;
                  font-weight: 600;
                  letter-spacing: 0.05em;
                }
                .chat-session-delete-btn {
                  opacity: 0;
                  transition: opacity 0.15s, color 0.15s;
                }
                .chat-session-item:hover .chat-session-delete-btn { opacity: 1; }
                .chat-session-delete-btn:hover { color: #e63757 !important; border-color: #e63757 !important; }
              `}</style>
              <div
                key="new"
                className="mb-4 p-3 border rounded bg-white d-flex align-items-center chat-session-new-btn"
                onClick={handleNewChat}
              >
                <i className="fe fe-plus-circle me-3 fs-4 text-muted" />
                <strong>
                  <FormattedMessage
                    id="app.components.chat.start_a_new_chat"
                    defaultMessage="New chat"
                  />
                </strong>
              </div>
              {sessions.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fe fe-message-square d-block mb-2 chat-session-empty-icon" />
                  <FormattedMessage
                    id="app.components.chat.no_sessions"
                    defaultMessage="You don't have any previous chat available"
                  />
                </div>
              ) : (
                <div className="mb-2 text-muted text-uppercase chat-session-label">
                  <FormattedMessage
                    id="app.components.chat.or_select_previous"
                    defaultMessage="Chats"
                  />
                </div>
              )}

              {sessions.map((session) => (
                <div
                  key={session.key}
                  className="p-3 mb-2 border rounded bg-white d-flex align-items-center chat-session-item"
                >
                  <div
                    className="d-flex align-items-center flex-fill"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSessionKey(session.key);
                      setCurrentScreen('chat');
                    }}
                  >
                    <i className="fe fe-message-circle me-3 text-muted flex-shrink-0" />
                    <div className="flex-fill">
                      <div className="chat-session-title">
                        {stripHtml(session.title) ||
                          formatMessage({
                            id: 'app.components.chat.session.untitled',
                            defaultMessage: 'Untitled Chat',
                          })}
                      </div>
                      <small
                        className="text-muted"
                        id={`session-time-${session.key}`}
                      >
                        {relativeDateTime({
                          date: new Date(session.created_at),
                        })}
                      </small>
                      <UncontrolledTooltip
                        target={`session-time-${session.key}`}
                        placement="bottom"
                      >
                        {new Date(session.created_at).toLocaleString(locale)}
                      </UncontrolledTooltip>
                    </div>
                    <i className="fe fe-chevron-right text-muted ms-2 flex-shrink-0 chat-session-chevron" />
                  </div>
                  <Button
                    size="sm"
                    color="white"
                    className="chat-session-delete-btn ms-2 flex-shrink-0"
                    title={formatMessage({
                      id: 'app.components.chat.session.delete',
                      defaultMessage: 'Delete chat',
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingSessionKey(session.key);
                    }}
                  >
                    <i className="fe fe-trash-2" />
                  </Button>
                </div>
              ))}
              {deletingSessionKey && (
                <ConfirmationDialogModal
                  isOpen={!!deletingSessionKey}
                  toggle={handleCancelDelete}
                  onClosed={handleCancelDelete}
                  title={formatMessage({
                    id: 'app.components.chat.delete.confirm_title',
                    defaultMessage: 'Delete chat',
                  })}
                  description={formatMessage({
                    id: 'app.components.chat.delete.confirm_description',
                    defaultMessage:
                      'Are you sure you want to permanently delete this chat? This action cannot be undone.',
                  })}
                  confirmText={formatMessage({
                    id: 'app.components.chat.delete.confirm',
                    defaultMessage: 'Delete',
                  })}
                  confirmCallback={handleConfirmDelete}
                />
              )}
            </div>
          )}
          {currentScreen === 'chat' && (
            <>
              <div
                className="card-body"
                style={{
                  overflowY: 'auto',
                  flex: 1,
                }}
              >
                {messages.map((message) =>
                  message.sender === 'user' ? (
                    <div
                      key={message.key}
                      className="d-flex mb-3 justify-content-end chat-message"
                    >
                      <div
                        className="ms-4 mb-2 p-2 rounded bg-light text-dark"
                        style={{ maxWidth: '100%' }}
                      >
                        <div onClick={handleMessageLinkClick}>
                          <MarkdownViewer
                            className="p-2 chat-user-bubble"
                            content={message.text}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={message.key} className="mb-3 chat-message">
                      <div onClick={handleMessageLinkClick}>
                        <MarkdownViewer content={message.text} />
                      </div>
                      {message.key !== 'initial' && (
                        <div className="chat-action-bar">
                          <button
                            className="chat-action-btn"
                            title={formatMessage({
                              id: 'app.components.chat.action.copy',
                              defaultMessage: 'Copy',
                            })}
                            onClick={() => {
                              const plainText = message.text
                                .replace(/<[^>]+>/g, '')
                                .trim();
                              navigator.clipboard.writeText(plainText);
                              toast.success(
                                formatMessage({
                                  id: 'app.components.chat.action.copied',
                                  defaultMessage: 'Copied to clipboard',
                                })
                              );
                            }}
                          >
                            <i className="fe fe-copy" />
                          </button>
                          <button
                            className={`chat-action-btn${
                              messageFeedback[message.key] === 'LIKE'
                                ? ' chat-action-active'
                                : ''
                            }`}
                            title={formatMessage({
                              id: 'app.components.chat.action.thumbs_up',
                              defaultMessage: 'Good response',
                            })}
                            onClick={() => handleFeedback(message.key, 'LIKE')}
                          >
                            <i className="fe fe-thumbs-up" />
                          </button>
                          <button
                            className={`chat-action-btn${
                              messageFeedback[message.key] === 'DISLIKE'
                                ? ' chat-action-active'
                                : ''
                            }`}
                            title={formatMessage({
                              id: 'app.components.chat.action.thumbs_down',
                              defaultMessage: 'Bad response',
                            })}
                            onClick={() =>
                              handleFeedback(message.key, 'DISLIKE')
                            }
                          >
                            <i className="fe fe-thumbs-down" />
                          </button>
                          <button
                            className="chat-action-btn chat-action-more"
                            title={relativeDateTime({
                              date: new Date(message.timestamp),
                            })}
                          >
                            <i className="fe fe-more-horizontal" />
                            <span className="chat-action-timestamp">
                              {new Date(message.timestamp).toLocaleString()}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  )
                )}
                {isNewSession && (
                  <div className="d-flex justify-content-start mb-3 mt-4">
                    <Button
                      color="white"
                      className="border"
                      onClick={handleBrowsePrompts}
                    >
                      <i className="fe fe-message-circle me-2" />
                      <FormattedMessage
                        id="app.components.chat.browse_prompts"
                        defaultMessage="Browse prompt suggestions"
                      />
                    </Button>
                  </div>
                )}
                {isLoading && (
                  <div className="d-flex justify-content-start mb-3">
                    <div
                      className="p-2 rounded bg-white border"
                      style={{ maxWidth: '75%' }}
                    >
                      <div className="text-muted">
                        <AILoader text={progressContent} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="card-footer bg-white d-flex align-items-stretch">
                <div
                  ref={chatInputRef}
                  className={`border rounded-start${
                    isLoading ? ' chat-input-loading' : ''
                  }`}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <RichTextEditor
                    className="chat-input-editor"
                    inForm={true}
                    name="chat-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder={formatMessage({
                      id: 'app.components.chat.placeholder.type_your_message',
                      defaultMessage: 'Ask anything',
                    })}
                    disabled={isLoading}
                    disableUnsavedChangesPrompt={true}
                    showToolbar={false}
                    config={{}}
                  />
                  <style>{`
                    .chat-input-editor .fr-wrapper { min-height: 0 !important; }
                    .chat-input-editor .fr-element { padding: 8px 8px 4px !important; min-height: 0 !important; max-height: 120px; overflow-y: auto; }
                    .chat-input-loading { background-color: #e9ecef; }
                    .chat-input-loading .fr-wrapper,
                    .chat-input-loading .fr-element { background-color: transparent !important; }
                    .chat-user-bubble p { margin-bottom: 0; }
                    .chat-message { position: relative; }
                    .chat-action-bar {
                      display: flex;
                      gap: 2px;
                      margin-top: 4px;
                      opacity: 0;
                      transition: opacity 0.15s;
                    }
                    .chat-message:hover .chat-action-bar { opacity: 1; }
                    .chat-action-btn {
                      background: none;
                      border: 1px solid transparent;
                      border-radius: 4px;
                      color: #95aac9;
                      cursor: pointer;
                      padding: 2px 6px;
                      font-size: 0.8rem;
                      line-height: 1;
                      position: relative;
                      transition: color 0.15s, background-color 0.15s;
                    }
                    .chat-action-btn:hover {
                      color: #12263f;
                      background-color: #f0f4f8;
                      border-color: #dee2e6;
                    }
                    .chat-action-btn.chat-action-active {
                      color: #2c7be5;
                      opacity: 1;
                    }
                    .chat-action-more .chat-action-timestamp {
                      display: none;
                      position: absolute;
                      bottom: 100%;
                      left: 50%;
                      transform: translateX(-50%);
                      background: #12263f;
                      color: #fff;
                      font-size: 0.7rem;
                      padding: 4px 8px;
                      border-radius: 4px;
                      white-space: nowrap;
                      margin-bottom: 4px;
                      pointer-events: none;
                    }
                    .chat-action-more:hover .chat-action-timestamp { display: block; }
                    .chat-message a[href^="/"] {
                      display: inline-block;
                      margin-top: 4px;
                      padding: 4px 12px;
                      border: 1px solid #dee2e6;
                      border-radius: 6px;
                      background-color: #f8f9fa;
                      color: #2c7be5;
                      text-decoration: none;
                      font-size: 0.875rem;
                      font-weight: 500;
                      cursor: pointer;
                      transition: background-color 0.15s, box-shadow 0.15s;
                    }
                    .chat-message a[href^="/"]:hover {
                      background-color: #e8f0fe;
                      box-shadow: 0 1px 4px rgba(0,0,0,0.1);
                    }
                    .chat-message a[href^="/"]::before {
                      content: "\\e92d";
                      font-family: "Feather";
                      margin-right: 6px;
                      font-size: 0.8rem;
                    }
                    .chat-message a[href*="/uploads/ai-files/"] {
                      display: inline-block;
                      margin-top: 4px;
                      padding: 6px 14px;
                      border: 1px solid #2c7be5;
                      border-radius: 6px;
                      background-color: #2c7be5;
                      color: #fff;
                      text-decoration: none;
                      font-size: 0.875rem;
                      font-weight: 500;
                      cursor: pointer;
                      transition: background-color 0.15s, box-shadow 0.15s;
                    }
                    .chat-message a[href*="/uploads/ai-files/"]:hover {
                      background-color: #1a68d1;
                      box-shadow: 0 1px 4px rgba(0,0,0,0.15);
                      color: #fff;
                    }
                    .chat-message a[href*="/uploads/ai-files/"]::before {
                      content: "\\e958";
                      font-family: "Feather";
                      margin-right: 6px;
                      font-size: 0.8rem;
                    }
                  `}</style>
                </div>
                <button
                  className="btn btn-dark rounded-0 rounded-end"
                  onClick={handleSendClick}
                  disabled={isLoading || !inputValue.trim()}
                >
                  {isLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    <i className="fe fe-send" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {!(isDocked && isOpen) && (
        <button
          className="btn btn-primary rounded-circle shadow-lg navbar-dark d-flex align-items-center justify-content-center"
          style={{ width: '60px', height: '60px' }}
          onClick={handleToggle}
        >
          {isOpen ? (
            <i className="fe fe-x" style={{ fontSize: '20px' }}></i>
          ) : (
            <span className="position-relative d-inline-block">
              <i
                className="fe fe-message-square"
                style={{ fontSize: '36px', opacity: 0.8 }}
              ></i>
              <span
                className="position-absolute top-50 start-50 translate-middle"
                style={{ fontSize: '16px', fontWeight: 300 }}
              >
                <FormattedMessage
                  id="app.components.chat.float_button_label"
                  defaultMessage="AI"
                />
              </span>
            </span>
          )}
        </button>
      )}
    </div>
  );
};

export default React.memo(Chat);
